import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import * as webApiClient from 'web-api-client'
import { Customer, CustomError, SettingName } from 'web-api-client'
import useBitcoinPrice from 'hooks/useBitcoinPrice'
import useDetailedBetResult, {
  DetailedBetResult,
} from 'hooks/useDetailedBetResult'

export enum View {
  RACE = 'Race',
  LEADERBOARD = 'Leaderboard',
  SHARE = 'Share',
}

export interface PointsSettings {
  win: string
  referral: string
}

type AppContextType = {
  view: View
  setView: (view: View) => void
  isModalOpen: boolean
  setIsModalOpen: (value: boolean) => void
  price: number | null
  betCount: number
  setBetCount: (value: number) => void
  isRefueling: boolean
  customer: Customer | null
  setCustomer: (customer: Customer) => void
  pointsSettings: PointsSettings | null
  showSplashScreen: boolean
  setShowSplashScreen: (value: boolean) => void
} & DetailedBetResult

export const AppContext = createContext<AppContextType | null>(null)

export const useAppContext = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const webApp = window.Telegram?.WebApp
  const userData = webApp?.initDataUnsafe?.user

  const [view, setView] = useState<View>(View.RACE)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [betCount, setBetCount] = useState(10)
  const [isRefueling, setIsRefueling] = useState(false)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [pointsSettings, setPointsSettings] = useState<PointsSettings | null>(
    null
  )
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  const price = useBitcoinPrice()
  const detailedBetResult = useDetailedBetResult()

  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  )
  const referralCode = queryParams.get('referral_code')

  const saveCustomer = useCallback(async () => {
    if (!userData) return

    try {
      const newCustomer = await webApiClient.saveCustomer({
        telegram_id: `${userData.id}`,
        first_name: userData.first_name,
        last_name: userData?.last_name ?? '',
        username: userData?.username ?? '',
        photo_url: userData?.photo_url ?? '',
      })

      setCustomer(newCustomer)
    } catch (error) {
      console.log('error', error)
    }
  }, [userData])

  const getSettings = useCallback(async () => {
    try {
      const { items } = await webApiClient.getSettings()

      const winPoints =
        items.find((item) => item.name === SettingName.WIN_POINTS)?.value ?? '0'
      const referralPoints =
        items.find((item) => item.name === SettingName.REFERRAL_POINTS)
          ?.value ?? '0'

      setPointsSettings({
        win: winPoints,
        referral: referralPoints,
      })
    } catch (error) {
      console.log('error', error)
    }
  }, [])

  const addReferrals = useCallback(async () => {
    if (!customer || !referralCode) return

    try {
      await webApiClient.addReferrals({
        new_customer_id: customer.id,
        referral_code: referralCode,
      })
    } catch (error) {
      console.log('error', error)
    } finally {
      queryParams.delete('referral_code')

      const newUrl =
        queryParams.toString() === ''
          ? window.location.pathname
          : `${window.location.pathname}?${queryParams.toString()}`

      window.history.replaceState({}, document.title, newUrl)
    }
  }, [customer, queryParams, referralCode])

  const getCustomer = useCallback(async () => {
    if (!userData) return

    try {
      const customer = await webApiClient.getCustomer(`${userData.id}`)

      setCustomer(customer)
    } catch (error) {
      if ((error as CustomError).response.data.error === 'Customer not found') {
        saveCustomer()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveCustomer, userData])

  useEffect(() => {
    webApp.expand()

    getCustomer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webApp])

  useEffect(() => {
    if (betCount === 0) setIsRefueling(true)

    const timer = setTimeout(() => {
      setIsRefueling(false)
      setBetCount(10)
    }, 35000)

    return (): void => clearTimeout(timer)
  }, [betCount])

  const addPoints = async () => {
    if (!customer) return

    try {
      const updatedCustomer = await webApiClient.addPoints(customer.telegram_id)

      setCustomer(updatedCustomer)
    } catch (error) {
      console.log('error', error)
    }
  }

  const removePoints = async () => {
    if (!customer) return

    try {
      const updatedCustomer = await webApiClient.removePoints(
        customer.telegram_id
      )

      setCustomer(updatedCustomer)
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    if (detailedBetResult.betResult === 'win') {
      addPoints()
    } else if (detailedBetResult.betResult === 'lose') {
      removePoints()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedBetResult.betResult])

  useEffect(() => {
    if (referralCode && customer) {
      addReferrals()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode, customer])

  useEffect(() => {
    getSettings()
  }, [getSettings])

  const value = useMemo(
    () => ({
      view,
      setView,
      isModalOpen,
      setIsModalOpen,
      price,
      betCount,
      setBetCount,
      isRefueling,
      customer,
      setCustomer,
      pointsSettings,
      showSplashScreen,
      setShowSplashScreen,
      ...detailedBetResult,
    }),
    [
      view,
      setView,
      isModalOpen,
      setIsModalOpen,
      price,
      betCount,
      setBetCount,
      isRefueling,
      detailedBetResult,
      customer,
      setCustomer,
      pointsSettings,
      showSplashScreen,
      setShowSplashScreen,
    ]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export default AppProvider
